/**
 * Interactive tutorials — the bootstrap, called from BOTH entry points.
 *
 * The tour spans two bundles that share nothing: the Events index is ERB and
 * loads entrypoints/application.js (Turbo + Stimulus, no Vue app), while the
 * event editor loads entrypoints/vue.ts (Vue, and no Turbo at all). A Stimulus
 * controller would silently not exist on the editor, where most of the tour
 * happens, and a component mounted by the editor's Vue app would not exist on
 * the index, where it starts (docs/INTERACTIVE_TUTORIALS_PLAN.md §1.1).
 *
 * So the engine is its own tiny Vue app in a container it creates itself, with
 * no dependency on Turbo or Stimulus. Vue is a plain npm package; importing
 * createApp from a Stimulus bundle is unremarkable.
 *
 * Mounting starts NOTHING. It restores a run that was already in progress and
 * otherwise renders an empty div, waiting for a call to the API below.
 */

import { createApp, type App } from 'vue'
import TourRoot from './TourRoot.vue'
import { initTutorialDrawer } from './drawer'
import { TOURS } from './registry'
import type { TourPhase } from './state'

const ROOT_ID = 'bz-tour-root'

/** The tour a call means when it doesn't say — there is one today. */
const DEFAULT_TOUR_ID = TOURS[0].id

/**
 * What the engine exposes to the rest of the app, and to the console.
 *
 * Every method is safe to call at any time and on any page: an unknown id or a
 * missing engine is a warning, never a throw.
 */
export interface BlumeTourApi {
  /** Begin at checkpoint 0 and persist the run across page loads. */
  startTour(tourId?: string): boolean
  /** End the run, drop the stored state, show the "tutorial ended" toast. */
  exitTour(): void
  /** Show the Welcome card. Wired to the auto-start gate in a later slice. */
  showWelcome(tourId?: string, options?: { firstName?: string }): boolean
  /** Show the Completion card without recording a completion. */
  showDone(tourId?: string): boolean
  /** Record the completion server-side and show the Completion card. */
  completeTour(): void
  /** Current phase / tour / checkpoint — for the console and for diagnosis. */
  state(): { phase: TourPhase | null; tourId: string | null; cpIndex: number }
}

interface TourRootExposed {
  startTour(id: string): boolean
  exitTour(): void
  showWelcome(id: string, options?: { firstName?: string }): boolean
  showDone(id: string): boolean
  completeTour(): void
  currentState(): { phase: TourPhase | null; tourId: string | null; cpIndex: number }
}

declare global {
  interface Window {
    __blumeTour?: BlumeTourApi
  }
}

let app: App | null = null
let root: HTMLElement | null = null
let vm: TourRootExposed | null = null

function unmount(): void {
  if (app) app.unmount()
  root?.remove()
  app = null
  root = null
  vm = null
}

/**
 * Create the container, mount the engine into it, and publish the API.
 *
 * Idempotent by the container's presence in the CURRENT document, not by a
 * module-level flag: a Turbo Drive visit on the ERB side replaces <body>
 * wholesale, taking the container with it while this module stays loaded. A
 * flag alone would leave the tour silently unmounted for the rest of the
 * session (docs/INTERACTIVE_TUTORIALS_PLAN.md §1.3).
 */
export function mountTutorial(): BlumeTourApi | null {
  if (root && document.body.contains(root)) return window.__blumeTour ?? null
  if (app) unmount()

  document.getElementById(ROOT_ID)?.remove()

  const el = document.createElement('div')
  el.id = ROOT_ID
  // .bz .bz-d2 are the design system's scope and direction; every token the
  // overlay reads is declared on them.
  el.className = 'bz bz-d2'
  document.body.appendChild(el)

  const instance = createApp(TourRoot)
  vm = instance.mount(el) as unknown as TourRootExposed
  app = instance
  root = el

  const api: BlumeTourApi = {
    startTour: (tourId = DEFAULT_TOUR_ID) => vm?.startTour(tourId) ?? false,
    exitTour: () => vm?.exitTour(),
    showWelcome: (tourId = DEFAULT_TOUR_ID, options = {}) =>
      vm?.showWelcome(tourId, options) ?? false,
    showDone: (tourId = DEFAULT_TOUR_ID) => vm?.showDone(tourId) ?? false,
    completeTour: () => vm?.completeTour(),
    state: () => vm?.currentState() ?? { phase: null, tourId: null, cpIndex: 0 },
  }

  window.__blumeTour = api

  // The Learn group's Start/Redo buttons. Bound here rather than from the entry
  // points because it needs the API published on the line above; the call is
  // idempotent, which matters because this function runs again on every
  // turbo:load while the listener it attaches survives one.
  initTutorialDrawer()

  return api
}

/**
 * Mount now if the document is ready, otherwise when it is — and again after a
 * Turbo Drive visit. `turbo:load` is listened for by NAME: adding a listener for
 * an event costs nothing on a page where Turbo isn't loaded, and importing Turbo
 * here would drag it into the editor bundle, which deliberately has none.
 */
export function bootTutorial(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountTutorial(), { once: true })
  } else {
    mountTutorial()
  }

  document.addEventListener('turbo:load', () => mountTutorial())
}
